// client/src/context/auth/AuthProvider.js - VERSÃO COMPLETA
import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useMe } from '../../hooks/useAuth';
import { 
    hasPermission as hasPermissionUtil, 
    isMatrizAdmin as isMatrizAdminUtil,
    isFranquiaAdmin as isFranquiaAdminUtil,
    isContaAdmin as isContaAdminUtil,
    getUserScope
} from '../../utils/permissions';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const { 
        user, 
        isLoading, 
        error,
        refetch,
        getAccountId,
        hasAccount,
        getCurrentFranquia,
        hasFranquia,
        needsPasswordChange,
        hasPermission: userHasPermission,
        getCurrentAccount,
        getCurrentRole,
        logout 
    } = useMe();

    // Função de permissão unificada
    const hasPermission = useCallback((permission) => {
        if (!user) return false;
        return userHasPermission ? userHasPermission(permission) : hasPermissionUtil(user, permission);
    }, [user, userHasPermission]);

    // Helper para verificar se usuário é Matriz Admin
    const isMatrizAdmin = useCallback(() => {
        return isMatrizAdminUtil(user);
    }, [user]);

    // Helper para verificar se usuário é Franquia Admin
    const isFranquiaAdmin = useCallback(() => {
        return isFranquiaAdminUtil(user);
    }, [user]);

    // Helper para verificar se usuário é Conta Admin
    const isContaAdmin = useCallback(() => {
        return isContaAdminUtil(user);
    }, [user]);

    // Helper para obter accountId válido
    const getValidAccountId = useCallback(() => {
        if (!user) return null;
        
        // Usar o getAccountId do hook useMe se disponível
        if (getAccountId) {
            const accountId = getAccountId();
            if (accountId) return accountId;
        }
        
        // Fallback para a função do permissions.js
        return getUserAccountIdFromPermissions(user);
    }, [user, getAccountId]);

    // Função auxiliar para obter accountId das permissões
    const getUserAccountIdFromPermissions = (user) => {
        if (!user) return null;
        
        if (user.accountId) return user.accountId;
        if (user.account) return user.account._id || user.account;
        if (user.unidadePrincipal?.account) return user.unidadePrincipal.account._id || user.unidadePrincipal.account;
        
        return null;
    };

    // Valor do contexto memoizado
    const value = useMemo(() => ({
        // Dados do usuário
        user,
        isLoading,
        error,
        isAuthenticated: !!user && !isLoading,
        
        // Ações
        refetch,
        logout: logout || (() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('token');
            window.location.href = '/login';
        }),
        
        // Permissões
        hasPermission,
        
        // Helpers de conta e franquia
        getAccountId: getValidAccountId,
        getCurrentAccount,
        getCurrentRole,
        getCurrentFranquia,
        hasAccount,
        hasFranquia,
        needsPasswordChange,
        
        // Helpers de tipo de admin
        isMatrizAdmin: isMatrizAdmin(),
        isFranquiaAdmin: isFranquiaAdmin(),
        isContaAdmin: isContaAdmin(),
        isAnyAdmin: isMatrizAdmin() || isFranquiaAdmin() || isContaAdmin(),
        
        // Compatibilidade
        accountId: getValidAccountId(),
        franquiaId: getCurrentFranquia ? getCurrentFranquia()?._id : user?.franquiaId,
        
        // Informações do escopo
        scope: getUserScope(user) || 'conta',
        userScope: getUserScope(user) || 'conta',
        adminType: user?.adminType || user?.role?.name || null,
        
        // Dados adicionais para debug
        permissionsCount: user?.role?.permissions?.length || 0,
        roleName: user?.role?.name,

        // ✅ Acesso de suporte (impersonate por concessão) — só existem
        // quando /auth/me ecoa a claim `impersonating` do token (ver
        // middlewares/auth.js::protect e authController.js::getMe no
        // server). Sobrevivem ao refetch de 60s/foco porque vêm do próprio
        // `user`, não de um estado guardado só no momento do impersonate.
        isImpersonating: !!user?.isImpersonating,
        originalAdminId: user?.originalAdminId || null,
        originalAdminName: user?.originalAdminName || null,
        impersonationExpiresAt: user?.impersonationExpiresAt || null
    }), [
        user, 
        isLoading, 
        error,
        refetch,
        logout,
        hasPermission,
        getValidAccountId,
        getCurrentAccount,
        getCurrentRole,
        getCurrentFranquia,
        hasAccount,
        hasFranquia,
        needsPasswordChange,
        isMatrizAdmin,
        isFranquiaAdmin,
        isContaAdmin
    ]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
};

// Exportação do contexto para uso direto (opcional)
export { AuthContext };